// Accessibility.js - Accessibility utilities and components

import React from 'react';
import styled from 'styled-components';

// ============ Screen Reader Only ============
// Visually hidden but accessible to screen readers
export const ScreenReaderOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

// ============ Skip Link ============
// Skip to main content link for keyboard navigation
const SkipLinkStyled = styled.a`
  position: fixed;
  top: -100px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000;
  padding: 12px 24px;
  background: #0093E9;
  color: white;
  font-weight: 600;
  font-size: 14px;
  text-decoration: none;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: top 0.2s ease;

  &:focus {
    top: 0;
    outline: 3px solid #ffcc00;
    outline-offset: 2px;
  }

  &:hover {
    background: #007acc;
  }
`;

export const SkipLink = ({ targetId = 'main-content', children = 'Skip to main content' }) => (
  <SkipLinkStyled href={`#${targetId}`}>
    {children}
  </SkipLinkStyled>
);

// ============ Live Region ============
// For announcing dynamic content changes to screen readers
const LiveRegionStyled = styled.div`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export const LiveRegion = ({
  message,
  politeness = 'polite', // 'polite' or 'assertive'
  atomic = true,
}) => (
  <LiveRegionStyled
    role="status"
    aria-live={politeness}
    aria-atomic={atomic}
  >
    {message}
  </LiveRegionStyled>
);

// ============ Focus Trap ============
// Utility hook for focus management in modals
export const useFocusTrap = (isActive, containerRef) => {
  React.useEffect(() => {
    if (!isActive || !containerRef.current) return;

    const container = containerRef.current;
    const focusableElements = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleKeyDown = (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    // Focus first element when trap activates
    firstElement?.focus();

    container.addEventListener('keydown', handleKeyDown);
    return () => container.removeEventListener('keydown', handleKeyDown);
  }, [isActive, containerRef]);
};

// ============ Announce ============
// Utility to announce messages to screen readers
let announceContainer = null;

export const announce = (message, politeness = 'polite') => {
  if (typeof document === 'undefined') return;

  // Create container if it doesn't exist
  if (!announceContainer) {
    announceContainer = document.createElement('div');
    announceContainer.setAttribute('aria-live', politeness);
    announceContainer.setAttribute('aria-atomic', 'true');
    announceContainer.setAttribute('role', 'status');
    announceContainer.style.cssText = `
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    `;
    document.body.appendChild(announceContainer);
  }

  // Clear and set message
  announceContainer.textContent = '';
  setTimeout(() => {
    announceContainer.textContent = message;
  }, 100);
};

// ============ Focus Visible ============
// Styled component that only shows focus ring on keyboard navigation
export const FocusVisible = styled.button`
  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 3px solid #0093E9;
    outline-offset: 2px;
  }
`;

// ============ Accessible Button ============
// Button with proper accessibility attributes
const AccessibleButtonStyled = styled.button`
  cursor: pointer;
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  font: inherit;
  color: inherit;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 3px solid #0093E9;
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const AccessibleButton = ({
  children,
  onClick,
  disabled,
  ariaLabel,
  ariaExpanded,
  ariaControls,
  ariaPressed,
  type = 'button',
  ...props
}) => (
  <AccessibleButtonStyled
    type={type}
    onClick={onClick}
    disabled={disabled}
    aria-label={ariaLabel}
    aria-expanded={ariaExpanded}
    aria-controls={ariaControls}
    aria-pressed={ariaPressed}
    {...props}
  >
    {children}
  </AccessibleButtonStyled>
);

// ============ Accessible Link ============
// Link that opens in new tab with proper warning
const ExternalLinkStyled = styled.a`
  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 3px solid #0093E9;
    outline-offset: 2px;
  }
`;

export const ExternalLink = ({ href, children, ...props }) => (
  <ExternalLinkStyled
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    {...props}
  >
    {children}
    <ScreenReaderOnly> (opens in new tab)</ScreenReaderOnly>
  </ExternalLinkStyled>
);

// ============ Accessible Icon Button ============
// Icon button with required aria-label
export const IconButton = ({
  icon: Icon,
  ariaLabel,
  onClick,
  size = 24,
  ...props
}) => (
  <AccessibleButton
    onClick={onClick}
    ariaLabel={ariaLabel}
    {...props}
  >
    {typeof Icon === 'function' ? <Icon size={size} aria-hidden="true" /> : Icon}
  </AccessibleButton>
);

// ============ Heading Level Manager ============
// Context for managing heading hierarchy
const HeadingLevelContext = React.createContext(1);

export const HeadingLevelProvider = ({ level, children }) => (
  <HeadingLevelContext.Provider value={level}>
    {children}
  </HeadingLevelContext.Provider>
);

export const useHeadingLevel = () => React.useContext(HeadingLevelContext);

export const DynamicHeading = ({ children, className, ...props }) => {
  const level = useHeadingLevel();
  const Tag = `h${Math.min(level, 6)}`;
  return <Tag className={className} {...props}>{children}</Tag>;
};

// ============ Loading State ============
// Accessible loading indicator
const LoadingSpinnerStyled = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;

  .spinner {
    width: ${({ $size }) => $size || '20px'};
    height: ${({ $size }) => $size || '20px'};
    border: 2px solid #e0e0e0;
    border-top-color: #0093E9;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

export const LoadingSpinner = ({ size, label = 'Loading...' }) => (
  <LoadingSpinnerStyled $size={size} role="status" aria-label={label}>
    <div className="spinner" aria-hidden="true" />
    <ScreenReaderOnly>{label}</ScreenReaderOnly>
  </LoadingSpinnerStyled>
);

export default {
  ScreenReaderOnly,
  SkipLink,
  LiveRegion,
  useFocusTrap,
  announce,
  FocusVisible,
  AccessibleButton,
  ExternalLink,
  IconButton,
  HeadingLevelProvider,
  useHeadingLevel,
  DynamicHeading,
  LoadingSpinner,
};
